import React from 'react';
import type { VcsApi } from '@env0/vcs-service/api';
import ControlledSelect from 'components/common/form-controlled/controlled-select';
import { SelectOption } from 'components/common/input-components/select';
import { FieldRow } from 'components/common/form-components';
import type { VCSForm } from 'components/templates/templates-wizard/common/template-wizard.types';
import { useCachedVcsRepository, useGetVcsConnections } from 'stores/rq/vcs-connections';
import { useVcsRepositoriesFirstPage, useVcsRepositoryLookup } from './use-vcs-repositories-search.hook';

type Props = {
  form: VCSForm;
  vcsType: VcsApi.VcsType;
};

const VcsConnectionSelectField: React.FunctionComponent<Props> = ({ form, vcsType }) => {
  // Named watches: `watch()` only reports registered fields once any field registers, and the repository input
  // may live in another component (or nowhere, in edit mode) while this one is mounted.
  const repository = form.watch('repository');
  const vcsConnectionId = form.watch('vcsConnectionId');
  const firstPage = useVcsRepositoriesFirstPage(vcsType, 'DeploymentPipeline');
  // The first page, or the search result the repository was just picked from, usually holds it already. Only a
  // repository pinned on an existing template and outside the first page needs its own request.
  const cachedRepository = useCachedVcsRepository(vcsType, 'DeploymentPipeline', repository);
  const lookup = useVcsRepositoryLookup(
    vcsType,
    'DeploymentPipeline',
    repository,
    !firstPage.isPending && !cachedRepository
  );
  // Only users who can manage connections may list them; everyone else sees the id when the name is unknown.
  const { data: organizationConnections } = useGetVcsConnections();

  const selectedRepository =
    cachedRepository ?? lookup.data?.repositories.find(({ httpsUrl }) => httpsUrl === repository);
  const reachableConnections: VcsApi.RepositoryVcsConnection[] = selectedRepository
    ? (selectedRepository.vcsConnections ?? [
        { id: selectedRepository.vcsConnectionId, name: selectedRepository.connectionName }
      ])
    : [];

  // A pinned connection that no longer reaches the repository (or a repository neither query returns) is still
  // shown, so the user can see what the template is pinned to and move it to a connection that does reach.
  const isPinnedListed = reachableConnections.some(({ id }) => id === vcsConnectionId);
  const connections: Array<VcsApi.RepositoryVcsConnection & { isUnreachable?: boolean }> =
    vcsConnectionId && !isPinnedListed
      ? [
          ...reachableConnections,
          {
            id: vcsConnectionId,
            name: organizationConnections?.find(({ id }) => id === vcsConnectionId)?.name ?? vcsConnectionId,
            isUnreachable: true
          }
        ]
      : reachableConnections;

  if (connections.length === 0) return null;

  return (
    <FieldRow zeroBottomMargin>
      <ControlledSelect
        form={form}
        name="vcsConnectionId"
        inline
        width={50}
        label={{ id: 'templates.add.new.field.vcs-connection' }}
        info={{ id: 'templates.add.new.field.vcs-connection.info' }}
        data-e2e="vcs-connection-selector"
        loading={firstPage.isFetching || lookup.isFetching}
        disabled={connections.length < 2}>
        {connections.map(({ id, name, isUnreachable }) => (
          <SelectOption key={id} value={id} disabled={isUnreachable} data-e2e={`vcs-connection-option-${id}`}>
            {name}
          </SelectOption>
        ))}
      </ControlledSelect>
    </FieldRow>
  );
};

export default VcsConnectionSelectField;
